import type { Action, Reducer } from "@reduxjs/toolkit";
import {
    ProfilingState,
    ProfilingActionTypes,
    CycleEntry,
    PROFILING_SET_ENABLED,
    PROFILING_SET_REACT_RENDER_ENABLED,
    PROFILING_BATCH_RECEIVED,
    PROFILING_SELECT_CYCLE,
    PROFILING_CLEAR,
} from "./types";

// The enable flags persist across sessions (mirrors the `menu-expanded` pattern in app/reducers.ts).
// Batch data is deliberately session-only — it is large and ephemeral.
const PROFILING_ENABLED_KEY = "profiling-enabled";
const PROFILING_REACT_RENDER_KEY = "profiling-react-render";

const initialState: ProfilingState = {
    enabled: localStorage.getItem(PROFILING_ENABLED_KEY) === "1",
    reactRenderEnabled: localStorage.getItem(PROFILING_REACT_RENDER_KEY) === "1",
    groupOrder: [],
    groupCycleIds: {},
    cyclesById: {},
    startedAtPerf: null,
    selectedCycleId: null,
    maxGroups: 50,
};

// Nearly every template lives under this prefix; stripping it once at ingestion keeps every consumer
// (tree rows, tooltips, filter options/matching, aggregate keys, exports) consistent while freeing
// horizontal space in the UI.
const TEMPLATE_PREFIX = "src/templates/";

export const stripTemplatePrefix = (fileName: string | undefined): string | undefined =>
    fileName?.startsWith(TEMPLATE_PREFIX) ? fileName.slice(TEMPLATE_PREFIX.length) : fileName;

/**
 * Resolve the group id (root cycleId) for a cycle by walking `parentCycleId` up through the cycles we
 * already hold, to the topmost *present* ancestor.  A `null` parent means the cycle is itself a root
 * (the page-root Canvas parser).  If an ancestor is missing (e.g. evicted), the deepest present node
 * becomes the root so eviction-orphaned children still surface as their own group.
 *
 * `parentCycleId` references a cycleId — exactly how we key `cyclesById` — so this is a direct lookup,
 * no secondary index. Render batches arrive parent-before-child within a pass (they are buffered and
 * flushed in render order by `useProfilingSink`), so a child's parent cycle already exists here.
 */
function resolveRoot(cycleId: string, parentCycleId: string | null, cyclesById: Record<string, CycleEntry>): string {
    let root = cycleId;
    let parent = parentCycleId;
    let guard = 0;
    while (parent != null && guard++ < 10000) {
        const parentCycle = cyclesById[parent];
        if (!parentCycle) break;
        root = parentCycle.cycleId;
        parent = parentCycle.parentCycleId;
    }
    return root;
}

export const profilingReducer: Reducer<ProfilingState, Action, ProfilingState> = (
    state = initialState,
    action: ProfilingActionTypes
): ProfilingState => {
    switch (action.type) {
        case PROFILING_SET_ENABLED:
            localStorage.setItem(PROFILING_ENABLED_KEY, action.enabled ? "1" : "0");
            // Stamp the attribution floor only when a FRESH session starts: re-enabling with passes
            // retained must not orphan the oldest retained pass's event window (see types.ts).
            return {
                ...state,
                enabled: action.enabled,
                startedAtPerf: action.enabled && state.groupOrder.length === 0 ? action.atPerf : state.startedAtPerf,
            };

        case PROFILING_SET_REACT_RENDER_ENABLED:
            localStorage.setItem(PROFILING_REACT_RENDER_KEY, action.enabled ? "1" : "0");
            return { ...state, reactRenderEnabled: action.enabled };

        case PROFILING_BATCH_RECEIVED: {
            // Normalize the template path on the stored batch itself — downstream code reads
            // `fileName` both from the cycle entry and from the raw batches (aggregation, tooltips).
            const batch = { ...action.batch, filePath: action.batch.fileName, fileName: stripTemplatePrefix(action.batch.fileName) };
            const existing = state.cyclesById[batch.cycleId];

            // Merge the batch into its cycle by cycleId.  Batches for one cycle arrive across several
            // callbacks (render synchronously, then callbacks, then react on a later microtask), so the
            // entry is created on first sight and grown as later batches land.
            const cyclesById = { ...state.cyclesById };
            let groupOrder = state.groupOrder;
            let groupCycleIds = state.groupCycleIds;
            let selectedCycleId = state.selectedCycleId;

            if (existing) {
                const entry: CycleEntry = { ...existing, callbacks: existing.callbacks };
                if (batch.trigger === "render") entry.render = batch;
                else if (batch.trigger === "callback") entry.callbacks = [...entry.callbacks, batch];
                else if (batch.trigger === "react") entry.react = batch;
                cyclesById[batch.cycleId] = entry;
                return { ...state, cyclesById };
            }

            // New cycle: correlate it into a group and record it.
            const groupId = resolveRoot(batch.cycleId, batch.parentCycleId, cyclesById);
            const entry: CycleEntry = {
                cycleId: batch.cycleId,
                parentCycleId: batch.parentCycleId,
                groupId,
                filePath: batch.filePath,
                fileName: batch.fileName,
                startTime: batch.startTime,
                callbacks: [],
            };
            if (batch.trigger === "render") entry.render = batch;
            else if (batch.trigger === "callback") entry.callbacks = [batch];
            else if (batch.trigger === "react") entry.react = batch;
            cyclesById[batch.cycleId] = entry;

            const existingGroup = groupCycleIds[groupId];
            if (existingGroup) {
                groupCycleIds = { ...groupCycleIds, [groupId]: [...existingGroup, batch.cycleId] };
            } else {
                groupOrder = [...groupOrder, groupId];
                groupCycleIds = { ...groupCycleIds, [groupId]: [batch.cycleId] };

                // Ring-buffer eviction: drop the oldest whole groups (and all their cycles) beyond maxGroups.
                if (groupOrder.length > state.maxGroups) {
                    const evicted = groupOrder.slice(0, groupOrder.length - state.maxGroups);
                    groupOrder = groupOrder.slice(groupOrder.length - state.maxGroups);
                    groupCycleIds = { ...groupCycleIds };
                    for (const evictedGroupId of evicted) {
                        for (const cid of groupCycleIds[evictedGroupId] ?? []) {
                            delete cyclesById[cid];
                            if (selectedCycleId === cid) selectedCycleId = null;
                        }
                        delete groupCycleIds[evictedGroupId];
                    }
                }
            }

            return { ...state, cyclesById, groupOrder, groupCycleIds, selectedCycleId };
        }

        case PROFILING_SELECT_CYCLE:
            return { ...state, selectedCycleId: action.cycleId };

        case PROFILING_CLEAR:
            // The floor moves to the clear point so the first post-clear pass can't cite older events.
            return { ...state, groupOrder: [], groupCycleIds: {}, cyclesById: {}, selectedCycleId: null, startedAtPerf: action.atPerf };

        default:
            return state;
    }
};
